import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, Gauge, Lightbulb, Layers, Activity } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Table,
  type BadgeTone,
  type TableColumn,
} from '../../ui';
import type {
  ComponentInsight,
  EffortInsight,
  SuggestedInput,
} from '../../features/knowledge/types';
import {
  complexityBarClass,
  complexityLabel,
  complexityTone,
} from './format';

interface EffortInsightPanelProps {
  insight: EffortInsight;
}

const SIGNAL_TONE: Record<
  NonNullable<EffortInsight['signals'][number]['tone']>,
  BadgeTone
> = {
  neutral: 'neutral',
  info: 'info',
  warning: 'warning',
  danger: 'danger',
};

function formatValue(value: SuggestedInput['value']): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number')
    return Number(value.toFixed(4)).toLocaleString(undefined, {
      maximumFractionDigits: 4,
    });
  return value;
}

/**
 * Renders a package's synthetic effort analysis: a complexity meter, a
 * three-point day estimate, per-component breakdown, contributing signals, and
 * the suggested estimation inputs. The primary CTA pushes those suggested
 * inputs into the Estimation wizard via router state (strict layering: this is
 * plain navigation — no estimation store/api import).
 */
export function EffortInsightPanel({ insight }: EffortInsightPanelProps) {
  const navigate = useNavigate();

  const componentColumns = useMemo<TableColumn<ComponentInsight>[]>(
    () => [
      {
        key: 'name',
        header: 'Component',
        render: (c) => (
          <div>
            <div className="font-medium text-zinc-100">{c.name}</div>
            {c.path && (
              <div className="font-mono text-xs text-zinc-500">{c.path}</div>
            )}
          </div>
        ),
      },
      {
        key: 'complexity',
        header: 'Complexity',
        render: (c) => (
          <Badge tone={complexityTone(c.complexity)}>
            {complexityLabel(c.complexity)}
          </Badge>
        ),
      },
      {
        key: 'loc',
        header: 'LOC',
        align: 'right',
        render: (c) => (
          <span className="tabular-nums text-zinc-400">
            {c.loc ? c.loc.toLocaleString() : '—'}
          </span>
        ),
      },
      {
        key: 'days',
        header: 'Est. days',
        align: 'right',
        render: (c) => (
          <span className="tabular-nums font-semibold text-zinc-100">
            {c.estimatedDays}
          </span>
        ),
      },
    ],
    [],
  );

  const useInEstimate = () => {
    const suggestedInputs: Record<string, string | number | boolean> = {};
    for (const s of insight.suggestedInputs) {
      suggestedInputs[s.key] = s.value;
    }
    navigate('/estimation/new', {
      state: {
        suggestedInputs,
        suggestedFrom: insight.packageName,
        suggestedTitle: `${insight.packageName} build estimate`,
      },
    });
  };

  const { optimistic, likely, pessimistic } = insight.estimatedEffortDays;

  return (
    <div className="flex flex-col gap-6">
      {/* Headline: complexity meter + three-point estimate */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Gauge className="h-4 w-4 text-accent" />
              Effort insight
            </span>
          </CardTitle>
          <Badge tone={complexityTone(insight.overallComplexity)} dot>
            {complexityLabel(insight.overallComplexity)} complexity
          </Badge>
        </CardHeader>
        <CardBody className="flex flex-col gap-5">
          <p className="text-sm leading-relaxed text-zinc-300">
            {insight.summary}
          </p>

          {/* Complexity meter */}
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
              <span>Complexity score</span>
              <span className="tabular-nums text-zinc-300">
                {insight.complexityScore}/100
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-3">
              <div
                className={`h-full rounded-full ${complexityBarClass(insight.overallComplexity)}`}
                style={{ width: `${insight.complexityScore}%` }}
              />
            </div>
          </div>

          {/* Three-point estimate */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Optimistic', value: optimistic, tone: 'text-emerald-300' },
              { label: 'Likely', value: likely, tone: 'text-zinc-50' },
              { label: 'Pessimistic', value: pessimistic, tone: 'text-amber-300' },
            ].map((cell) => (
              <div
                key={cell.label}
                className="rounded-xl border border-surface-border bg-surface-1 px-4 py-3 text-center"
              >
                <div className={`text-2xl font-semibold tabular-nums ${cell.tone}`}>
                  {cell.value}
                </div>
                <div className="mt-0.5 text-xs text-zinc-500">
                  {cell.label} · days
                </div>
              </div>
            ))}
          </div>

          <div>
            <Button
              leftIcon={<Calculator className="h-4 w-4" />}
              onClick={useInEstimate}
            >
              Use in estimate
            </Button>
            <p className="mt-1.5 text-xs text-zinc-500">
              Prefills the Estimation wizard with the suggested inputs below.
              Only keys matching your chosen template are applied.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Signals */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Activity className="h-4 w-4 text-zinc-500" />
              Effort signals
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap gap-2">
          {insight.signals.map((s) => (
            <Badge key={s.label} tone={s.tone ? SIGNAL_TONE[s.tone] : 'neutral'}>
              <span className="text-zinc-400">{s.label}:</span>
              <span className="ml-1 font-semibold">{s.value}</span>
            </Badge>
          ))}
        </CardBody>
      </Card>

      {/* Component breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Layers className="h-4 w-4 text-zinc-500" />
              Component breakdown ({insight.components.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <Table
            columns={componentColumns}
            rows={insight.components}
            rowKey={(c) => c.name}
            empty="No component breakdown available."
          />
        </CardBody>
      </Card>

      {/* Suggested inputs */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-accent" />
              Suggested estimate inputs
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col gap-2">
          {insight.suggestedInputs.length === 0 ? (
            <p className="text-sm text-zinc-500">No suggested inputs.</p>
          ) : (
            insight.suggestedInputs.map((s) => (
              <div
                key={s.key}
                className="flex items-start justify-between gap-4 rounded-xl border border-surface-border bg-surface-1 px-4 py-2.5"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-100">
                      {s.label}
                    </span>
                    <span className="font-mono text-xs text-zinc-500">
                      {s.key}
                    </span>
                  </div>
                  {s.rationale && (
                    <p className="mt-0.5 text-xs text-zinc-500">{s.rationale}</p>
                  )}
                </div>
                <span className="shrink-0 rounded-lg bg-surface-3 px-2.5 py-1 text-sm font-semibold tabular-nums text-accent-fg">
                  {formatValue(s.value)}
                </span>
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default EffortInsightPanel;
